import { NavigationListItem as NavigationListItemType } from '@/schemas/sceneDataSchema';
import { RouteFn } from '@/hooks/use-flow';
import { Box, Icon, styled, Typography } from '@mui/material';
import { TypographyColors } from '@/constants/typographyTypes';
import { ChipColors, ChipSizes, ChipVariant } from '@/constants/chipTypes';
import { InertChip } from '@/components/DynamicUI/InertChip';

interface NavigationListItemProps {
  component: NavigationListItemType;
  route: RouteFn;
}

export const NavigationListItem: React.FC<NavigationListItemProps> = ({
  component: {
    icon,
    icon_color,
    overline,
    label1,
    label2,
    supporting_text,
    with_dividers,
    icon_right,
    entity_index,
    step_id,
    trailing_pill,
  },
  route,
}) => {
  const handleClick = () => {
    void route(entity_index, step_id);
  };

  return (
    <StyledListItem
      withDividers={!!with_dividers}
      onClick={handleClick}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: 'text.primary' }}>
        {icon && <Icon color={icon_color ?? undefined}>{icon}</Icon>}
        <TextGroup>
          {overline && (
            <Typography
              variant='overline'
              color={TypographyColors.TEXT_SECONDARY}
            >
              {overline}
            </Typography>
          )}
          <Typography variant='body1'>{label1}</Typography>
          {label2 && <Typography variant='body1'>{label2}</Typography>}
          {supporting_text && (
            <Typography
              variant='body2'
              sx={{ whiteSpace: 'pre-line' }}
              color={TypographyColors.TEXT_SECONDARY}
            >
              {supporting_text}
            </Typography>
          )}
        </TextGroup>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {trailing_pill && (
          <InertChip
            label={trailing_pill}
            size={ChipSizes.SMALL}
            color={ChipColors.TEXT}
            variant={ChipVariant.FILLED}
          />
        )}
        {icon_right && (
          <Icon
            className={icon_right === 'chevron_right' ? 'chevron-right' : ''}
            color='secondary'
          >
            {icon_right}
          </Icon>
        )}
      </Box>
    </StyledListItem>
  );
};

const StyledListItem = styled('div')<{ withDividers?: boolean }>`
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 12px;
  /* Handle optional dividers between ListItems */
  ${({ withDividers }) => withDividers && 'border-bottom: 1px solid var(--mui-palette-border-main);'}
  &:last-of-type {
    border-bottom: none;
  }
  /* Animate chevron icon */
  .chevron-right {
    transition: transform 0.2s var(--ease-out-quart);
  }
  &:hover {
    .chevron-right {
      transform: translateX(4px);
    }
  }
`;

const TextGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 4px;
  user-select: none;
  & > span,
  & > p {
    line-height: 1;
  }
`;
